<template>
  <div class="image-uploader-wrapper">
    <v-row
      align="center"
      class="mb-2">
      <v-col
        v-if="label"
        cols="auto"
        class="py-0">
        <v-skeleton-loader
          :loading="loading"
          min-width="100"
          type="text"
        >
          <label
            class="text-subtitle-1 grey--text text--darken-2"
            :class="{ 'font-weight-bold': labelBold }"
          >
            {{ label }}
            <span
              v-if="required"
              class="error--text">*</span>
          </label>
        </v-skeleton-loader>
      </v-col>
      <v-spacer />

      <v-col
        v-if="showButton"
        cols="auto"
        class="py-0">
        <v-dialog
          v-model="dialog"
          persistent
          max-width="600"
          @click:outside="setDialog(false)"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-skeleton-loader
              :loading="loading"
              type="button"
            >
              <v-btn
                color="primary"
                depressed
                small
                v-bind="attrs"
                v-on="on"
              >
                <v-icon
                  left
                  small>
                  mdi-cloud-upload
                </v-icon>
                Upload
              </v-btn>
            </v-skeleton-loader>
          </template>

          <v-card class="rounded-lg">
            <v-card-title class="headline px-6 pt-5">
              <span>Upload Files</span>
              <v-spacer />
              <v-btn
                icon
                @click="setDialog(false)">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="px-6 pb-6">
              <div class="dialog-upload-zone mt-4">
                <b-upload
                  v-model="upload"
                  v-bind="$attrs.upload"
                  drag-drop
                  :multiple="multiple"
                  class="full-width-upload"
                  @input="handleUploadFile($event)"
                >
                  <div class="d-flex flex-column justify-center align-center py-10">
                    <v-icon
                      size="64"
                      color="primary lighten-2"
                      class="mb-3">
                      mdi-cloud-upload-outline
                    </v-icon>
                    <div class="text-h6 grey--text text--darken-1">
                      Drag files here
                    </div>
                    <span class="caption grey--text">{{ caption }}</span>
                  </div>
                </b-upload>
              </div>
            </v-card-text>
            <v-overlay
              :value="uploading"
              absolute
              color="white"
              opacity="0.8">
              <v-progress-circular
                size="64"
                color="primary"
                indeterminate></v-progress-circular>
            </v-overlay>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>

    <v-skeleton-loader
      :loading="loading"
      type="image"
      class="rounded-lg"
    >
      <div
        v-if="draggableEnable"
        class="img-preview-container"
        :class="{ 'has-error': !limit || (limit && draggableImg.length > limit) }"
      >
        <draggable
          v-model="draggableImg"
          tag="div"
          v-bind="dragOptions"
          class="image-grid"
          @start="setDragging(true)"
          @end="setDragging(false)"
        >
          <transition-group
            type="transition"
            name="flip-list"
            tag="div"
            class="d-flex flex-wrap"
          >
            <div
              v-for="(img, index) in draggableImg"
              :key="`draggableImg-${index}`"
              class="image-card-wrapper ma-2"
            >
              <div class="image-card elevation-2 rounded-lg">
                <v-img
                  v-if="img"
                  :aspect-ratio="imgAspectRatio"
                  :src="$convertImgix(`${img}`)"
                  :lazy-src="replaceURL($convertImgix(`${img}?w=10&h=10`))"
                  :width="150"
                  class="rounded-lg"
                  cover
                >
                  <div class="image-overlay d-flex justify-center align-center">
                    <v-btn
                      v-if="!disabled"
                      fab
                      small
                      color="error"
                      elevation="2"
                      @click.stop="removeImg(index)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-img>
              </div>
            </div>

            <div
              v-if="!limit || (limit && draggableImg.length < limit)"
              :key="`draggableImg-upload`"
              class="image-card-wrapper ma-2">
              <v-responsive
                :aspect-ratio="imgAspectRatio"
                :width="150"
                class="upload-tile rounded-lg"
              >
                <b-upload
                  v-model="upload"
                  v-bind="$attrs.upload"
                  drag-drop
                  :multiple="multiple"
                  class="full-wh-upload"
                  @input="handleUploadFile($event)"
                >
                  <div class="d-flex flex-column justify-center align-center full-height">
                    <v-icon
                      color="primary"
                      size="32">
                      mdi-plus
                    </v-icon>
                    <span class="caption primary--text mt-1 font-weight-bold">Add</span>
                  </div>
                </b-upload>
              </v-responsive>
            </div>
          </transition-group>
        </draggable>
      </div>

      <div
        v-else
        class="empty-dropzone rounded-lg"
      >
        <b-upload
          v-model="upload"
          v-bind="$attrs.upload"
          drag-drop
          :accept="accept"
          :multiple="multiple"
          class="full-width-upload"
          @input="handleUploadFile"
        >
          <div class="dropzone-content d-flex flex-column justify-center align-center py-8">
            <div class="icon-circle mb-3">
              <v-icon
                size="40"
                color="primary">
                mdi-image-plus
              </v-icon>
            </div>
            <div class="text-subtitle-1 font-weight-medium grey--text text--darken-2">
              {{ caption }}
            </div>
            <div class="caption grey--text text--lighten-1 mt-1 text-center">
              {{ multiple ? 'Drop files here' : 'Drop file here' }} or click to browse
            </div>
          </div>
        </b-upload>
      </div>
    </v-skeleton-loader>

    <v-text-field
      v-if="required"
      :value="draggableImg.join(',')"
      class="validation-anchor"
      :rules="[(v) => !!v || 'This field is required.']"
    />

    <v-slide-y-transition>
      <div
        v-if="limit && draggableImg.length > limit"
        class="d-flex align-center mt-2 error--text caption">
        <v-icon
          small
          color="error"
          class="mr-1">
          mdi-alert-circle
        </v-icon>
        Limit exceeded: You can only upload up to {{ limit }} images.
      </div>
    </v-slide-y-transition>
  </div>
</template>

<script>
// Logic remains 100% untouched as requested
import draggable from 'vuedraggable'
import { uploadToBucket } from '@/assets/js/AwsS3'

export default {
  components: {
    draggable
  },
  props: {
    label: {
      type: String,
      default: ''
    },
    multiple: {
      type: Boolean,
      default: false
    },
    value: {
      type: [Array, String],
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    imgAspectRatio: {
      type: Number,
      default: 1
    },
    s3Path: {
      type: String,
      required: true
    },
    validate: {
      type: Boolean,
      required: false
    },
    validateFunc: {
      type: Function,
      required: false,
      default: () => null
    },
    caption: {
      type: String,
      default: 'Click or Drop file for upload'
    },
    showButton: {
      type: Boolean,
      default: false
    },
    labelBold: {
      type: Boolean,
      default: false
    },
    needCompress: {
      type: Boolean,
      default: false
    },
    limit: {
      type: Number,
      default: 10
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      draggableImg: [],
      dialog: false,
      upload: null,
      uploading: false,
      isDragging: false,
      dragOptions: {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost'
      }
    }
  },
  computed: {
    transitionName () {
      return !this.isDragging ? 'flip-list' : null
    },
    draggableEnable () {
      return this.draggableImg.length > 0 && !!this.draggableImg[0]
    }
  },
  watch: {
    draggableImg (newVal) {
      if (this.multiple) {
        this.$emit('input', newVal)
      } else {
        this.$emit('input', newVal[0] ? newVal[0] : '')
      }
    },
    value (newVal) {
      if (this.multiple) {
        this.draggableImg = newVal
      } else {
        this.draggableImg = newVal ? [newVal] : []
      }
    }
  },
  created () {
    if (this.value) {
      if (Array.isArray(this.value)) {
        this.setDraggableImg(this.value)
      } else {
        this.setDraggableImg([this.value])
      }
    }
  },
  methods: {
    setUpload (value) {
      this.upload = value
    },
    setDraggableImg (value, push) {
      if (this.multiple && push) {
        this.draggableImg.push(...value)
      } else {
        this.draggableImg = new Array(...value)
      }
    },
    setDialog (value) {
      this.dialog = value
    },
    setDragging (value) {
      this.isDragging = value
    },
    replaceURL (value) {
      return value.replace(process.env.VUE_APP_S3_URL, `${process.env.VUE_APP_S3_IMGIX}/`)
    },
    removeImg (index) {
      if (this.draggableImg.length === 1) {
        this.draggableImg = []
      } else {
        this.draggableImg.splice(index, 1)
      }
    },
    async handleUploadFile (event) {
      let valid = true

      if (this.validate) {
        valid = await this.validateFunc(event)
      }

      if (valid) {
        let imgSrc = null
        if (this.multiple) {
          let diff = event.length

          if (this.limit && (event.length + this.draggableImg.length) > this.limit) {
            diff = this.limit - this.draggableImg.length
          }

          for (let i = 0; i < diff; i++) {
            if (!imgSrc || imgSrc.length === 0) {
              imgSrc = []
            }

            imgSrc.push(uploadToBucket(event[i], this.s3Path, this.needCompress))
          }

          imgSrc = await Promise.all(imgSrc)
        } else {
          imgSrc = [await uploadToBucket(event, this.s3Path, this.needCompress)]
        }

        this.setDraggableImg(imgSrc.map((r) => this.replaceURL(r.mediaUrl)), true)
        this.setDialog(false)
        this.setUpload(null)
      } else {
        console.warn('handleUploadFile valid false')
      }
    }
  }
}
</script>

<style lang="scss">
/* Wrapper to prevent styles leaking globally if not scoped */
.image-uploader-wrapper {

  /* Buefy Upload Override */
  .upload .upload-draggable {
    border: none !important; /* Remove Buefy default border */
    padding: 0 !important;
    display: block !important;
  }

  .full-width-upload {
    width: 100%;

    .upload-draggable {
      width: 100%;
    }
  }

  /* Hides the input slot but keeps the error message visible for required validation */
  .validation-anchor {
    margin: 0;
    padding: 0;
    min-height: 0;

    .v-input__slot {
      display: none;
    }

    .v-text-field__details {
      padding: 0;
      margin-top: 4px;
    }
  }

  /* Empty State Dropzone */
  .empty-dropzone {
    border: 2px dashed #e0e0e0;
    transition: all 0.3s ease;
    background-color: #fafafa;
    cursor: pointer;

    &:hover {
      border-color: var(--v-primary-base);
      background-color: #f0f7ff; // Light blue tint

      .icon-circle {
        transform: scale(1.1);
        background-color: white;
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      }
    }

    .icon-circle {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background-color: #f5f5f5;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    }
  }

  /* Image Cards */
  .image-card-wrapper {
    position: relative;
    cursor: grab;
  }
  .image-card-wrapper:active {
    cursor: grabbing;
  }

  .image-card {
    position: relative;
    overflow: hidden;
    transition: transform 0.2s;

    &:hover {
      transform: translateY(-2px);

      .image-overlay {
        opacity: 1;
      }
    }

    .image-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.4);
      opacity: 0;
      transition: opacity 0.2s ease-in-out;
      z-index: 2;
    }
  }

  /* Error State */
  .img-preview-container.has-error {
    .image-card {
      border: 2px solid #ff5252;
    }
  }

  /* Dialog styling */
  .dialog-upload-zone {
    border: 2px dashed #bdbdbd;
    border-radius: 8px;
    background: #fafafa;
    transition: background 0.3s;

    &:hover {
      background: #f5f5f5;
      border-color: var(--v-primary-base);
    }
  }

  .upload input[type="file"] {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0 !important; /* Make it invisible */
    outline: none;
    cursor: pointer;
    z-index: -1;
  }

  .upload-tile {
    border: 2px dashed #e0e0e0;
    background-color: #fafafa;
    transition: all 0.2s;
    cursor: pointer;
    overflow: hidden; /* Keeps content inside rounded corners */

    &:hover {
      border-color: var(--v-primary-base);
      background-color: #f0f7ff;
    }
  }

  .full-wh-upload {
    width: 100%;
    height: 100%;

    /* Target Buefy's internal classes to force full height */
    .upload, .upload-draggable {
      width: 100% !important;
      height: 100% !important;
      display: block; /* varying buefy versions need this */
    }
  }

  .full-height {
    height: 100%;
    width: 100%;
  }
}

/* Draggable Ghost class */
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
</style>
